import { useRef } from 'react';
import { OperationVariables, ApolloQueryResult, WatchQueryFetchPolicy } from '@apollo/client';
import { isDefined } from 'utils/tsHelpers';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useDispatch } from 'react-redux';
import { addNegativeToast } from 'modules/toastContainer';
import { useTranslations } from './useTranslations';
import { Asset, AssetRequestConfig, Pagination, Sort, SortableField } from '../types';
import { defaults } from '../constants';
import { FetchAllProjectAssetsQuery } from 'api/queries/creativeLibrary';
import { GraphQLResponsePaginationInfo } from 'modules/graphql';

type AccumulatedResults = {
  info: Omit<GraphQLResponsePaginationInfo, '__typename'>;
  results: Asset[];
};

export type FetchAssetsQueryResponse = {
  getImages: {
    info: Omit<GraphQLResponsePaginationInfo, '__typename'>;
    results: Asset[];
  };
};

export type useFetchAllProjectAssetsResponse = {
  info: Omit<GraphQLResponsePaginationInfo, '__typename'>;
  isLoading: boolean;
  hasError: boolean;
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
  results: Asset[];
};

export type useFetchAllProjectAssetsRequest = {
  search: string;
  sort: Sort;
  pagination: Pagination;
};

const generateFetchAssetQueryParams = (
  config: AssetRequestConfig,
): useFetchAllProjectAssetsRequest => ({
  search: config.search,
  sort: {
    sortDirection: config.sortDirection || defaults.sortDirection,
    sortBy: (config.sortBy as SortableField) || defaults.sortBy,
  },
  pagination: {
    offset: config.offset,
    limit: config.count,
  },
});

const defaultInfo = {
  count: 0,
  limit: defaults.pageSize,
  offset: 0,
  page: 1,
};

export function useFetchAllProjectAssetsQuery(
  config: AssetRequestConfig,
  options?: Record<string, any>,
): useFetchAllProjectAssetsResponse {
  const dispatch = useDispatch();
  const { t } = useTranslations();
  const accumulatedResults = useRef<AccumulatedResults>({
    info: defaultInfo,
    results: [],
  });
  const queryParams = generateFetchAssetQueryParams(config);

  const queryOptions = {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
    variables: {
      ...queryParams,
    },
    ...options,
    onError: () => dispatch(addNegativeToast(t('errors.fetchAssets'))),
  };

  const {
    data,
    isLoading,
    error: hasError,
    refetch,
  } = useGraphql(FetchAllProjectAssetsQuery, queryOptions);
  if (isDefined<FetchAssetsQueryResponse>(data)) {
    const { info, results } = data.getImages;

    /* Update the accumulatedAssets once loading has completed. Otherwise, just show the most
     * recent value. This eliminates the clearing of results while new (or additional) results are
     * loading. */
    if (!isLoading) {
      /* Changes to the search, sort, direction, etc. will reset the offset to 0, keeping the
       * accumulator clean */
      if (config.offset && config.append) {
        /* This check for a change in offset (from the last accumulated value) is to skip iterations
         * of this hook where nothing changes (to prevent re-appending the same results) */
        if (config.offset !== accumulatedResults.current.info.offset) {
          accumulatedResults.current = {
            info,
            results: [...accumulatedResults.current.results, ...(results || [])],
          };
        }
      } else {
        accumulatedResults.current = {
          info,
          results,
        };
      }
    }

    return {
      info: accumulatedResults.current.info,
      isLoading,
      hasError,
      refetch,
      results: accumulatedResults.current.results,
    };
  }

  /* Whenever the data is not defined, just show the most recent value. This eliminates the clearing
   * of results while new (or additional) results are loading. */

  return {
    info: accumulatedResults.current.info || defaultInfo,
    isLoading,
    hasError,
    refetch,
    results: accumulatedResults.current.results || [],
  };
}
